import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { GlobalState } from "store/configure-store";
import { connect } from "react-redux";
import {
  isPrescriptionSavedCareConnect,
  isPrescriptionSavedHelena,
  isPrescriptionSavedRecipe,
  isStoppedCareConnect,
  selectMedicationEntriesToCreate,
  selectPrescriptions,
} from "store/reducers/medication.reducer";
import { selectCanUseHelenaForMedication } from "store/reducers/helena.reducer";
import { MedicationEntryType } from "model/medication-entry.model";
import { usePrevious } from "common/hooks/previous.hook";
import { createContactWithPrescriptions } from "store/actions/contact.actions";
import { Col, Row } from "@cc/react-components/Grid";
import { ErrorType, ErrorTypeEnum } from "model/error.model";
import { PrescriptionType } from "model/prescription.model";
import {
  prescriptionsHE,
  prescriptionsRE,
  prescriptionsRE_HE,
} from "store/actions/prescription.actions";
import { selectPatient } from "store/reducers/patient.reducer";
import { selectUser } from "store/reducers/user-context.reducer";
import { StatusTypeEnum } from "common/enums/StatusTypeEnum";
import {
  Alert,
  Button,
  Card,
  Container,
  Icon,
  Spinner,
} from "@cc/react-components";
import Page from "ui/Page";
import { useSelector } from "react-redux";

export interface Props {
  inView: boolean;
}

const MedicationCreate = ({
  inView,
  createContact,
  prescriptionsRE_HE,
  prescriptionsRE,
  prescriptionsHE,
}: Props & PropsFromDispatch) => {
  const canUseHelena = useSelector((state: GlobalState) =>
    selectCanUseHelenaForMedication(state.helena)
  );
  const medicationEntries = useSelector((state: GlobalState) =>
    selectMedicationEntriesToCreate(state.medicationContext)
  );
  const isStoppedCC = useSelector((state: GlobalState) =>
    isStoppedCareConnect(state.medicationContext)
  );
  const isSavedCC = useSelector((state: GlobalState) =>
    isPrescriptionSavedCareConnect(state.medicationContext)
  );
  const isSavedRE = useSelector((state: GlobalState) =>
    isPrescriptionSavedRecipe(state.medicationContext)
  );
  const isSavedHE = useSelector((state: GlobalState) =>
    isPrescriptionSavedHelena(state.medicationContext)
  );
  const prescriptions = useSelector((state: GlobalState) =>
    selectPrescriptions(state.medicationContext)
  );
  const patientId = useSelector(
    (state: GlobalState) => selectPatient(state).id
  );
  const caregiverId = useSelector((state: GlobalState) => selectUser(state).id);

  const [t] = useTranslation();
  const [recipeError, setRecipeError] = useState("");
  const [helenaError, setHelenaError] = useState("");
  const [amountToCreate, setAmountToCreate] = useState(0);
  const [amountToStop, setAmountToStop] = useState(0);

  const previewInView = usePrevious(inView);
  useEffect(() => {
    if (
      !previewInView &&
      inView &&
      !!medicationEntries &&
      !(isSavedCC && isStoppedCC)
    ) {
      setRecipeError("");
      setHelenaError("");
      setAmountToCreate(
        medicationEntries.filter((me) => me.status === StatusTypeEnum.ACTIVE)
          .length
      );
      setAmountToStop(
        medicationEntries.filter((me) => me.status !== StatusTypeEnum.ACTIVE)
          .length
      );
      createContact(medicationEntries, canUseHelena).then().catch(parseError);
    }
  }, [
    inView,
    medicationEntries,
    previewInView,
    createContact,
    isStoppedCC,
    isSavedCC,
    canUseHelena,
  ]);

  const parseError = (reason?: ErrorType) => {
    if (reason && reason.type === ErrorTypeEnum.PRESCRIPTION_RECIPE) {
      setRecipeError(reason.message);
    } else if (reason && reason.type === ErrorTypeEnum.PRESCRIPTION_HELENA) {
      setHelenaError(reason.message);
    }
  };

  const renderLoading = (
    isSaved: boolean,
    errorMessage: string,
    key: string,
    options: object
  ) => {
    return (
      <Row className="align-items-center pb-2 no-gutters">
        <Col xs={2}>
          {errorMessage && (
            <Icon type="report" className="text-danger" size="lg" />
          )}
          {isSaved && !errorMessage && (
            <Icon type="success-circle" className="text-success" size="lg" />
          )}
          {!isSaved && !errorMessage && (
            <Spinner className="text-success ml-1" size="sm" />
          )}
        </Col>
        <Col xs={10}>
          <p className="my-1">{t(key, options)}</p>
        </Col>
      </Row>
    );
  };

  const retryPrescriptions = () => {
    if (recipeError) {
      setRecipeError("");
      if (canUseHelena) {
        prescriptionsRE_HE(patientId, caregiverId, prescriptions)
          .then()
          .catch(parseError);
      } else {
        prescriptionsRE(patientId, caregiverId, prescriptions)
          .then()
          .catch(parseError);
      }
    } else if (helenaError) {
      setHelenaError("");
      prescriptionsHE(patientId, caregiverId, prescriptions)
        .then()
        .catch(parseError);
    }
  };

  const loadingScreen = () => {
    return (
      <React.Fragment>
        <Page.Title className="font-weight-bold">
          {t("medicationFlow.create")}
        </Page.Title>
        <section>
          <Card>
            <Card.Body>
              <Container>
                {amountToCreate === 0 && amountToStop === 0 && (
                  <div className="p-4 text-center">
                    <Spinner
                      className="color-success"
                      animation="brand"
                      size="lg"
                    />
                  </div>
                )}
                {amountToStop > 0 &&
                  renderLoading(isStoppedCC, "", "medicationFlow.onStop", {
                    0: amountToStop,
                  })}
                {amountToCreate > 0 && (
                  <React.Fragment>
                    {renderLoading(isSavedCC, "", "medicationFlow.onCC", {
                      0: amountToCreate,
                    })}

                    {renderLoading(
                      isSavedRE,
                      recipeError,
                      "medicationFlow.onRE",
                      {}
                    )}

                    {canUseHelena &&
                      renderLoading(
                        isSavedHE,
                        helenaError || recipeError,
                        "medicationFlow.onHE",
                        {}
                      )}
                  </React.Fragment>
                )}
              </Container>
            </Card.Body>
          </Card>
        </section>
        {(helenaError || recipeError) && (
          <>
            <section>
              <Alert variant="danger">{t("medicationFlow.error.failed")}</Alert>
            </section>

            <section>
              <Button onClick={retryPrescriptions} variant="danger" block>
                <p className="m-0">{t("medicationFlow.retry")}</p>
              </Button>
            </section>
          </>
        )}
      </React.Fragment>
    );
  };

  return (
    <Page.Scrollable>
      <Page.Body>{loadingScreen()}</Page.Body>
    </Page.Scrollable>
  );
};

interface PropsFromDispatch {
  createContact: (
    medicationEntries: Array<MedicationEntryType>,
    canUseHelena: boolean
  ) => Promise<void>;
  prescriptionsRE_HE: (
    patientId: number,
    caregiverId: number,
    prescriptions: Array<PrescriptionType>
  ) => Promise<Array<PrescriptionType>>;
  prescriptionsRE: (
    patientId: number,
    caregiverId: number,
    prescriptions: Array<PrescriptionType>
  ) => Promise<Array<PrescriptionType>>;
  prescriptionsHE: (
    patientId: number,
    caregiverId: number,
    prescriptions: Array<PrescriptionType>
  ) => Promise<Array<PrescriptionType>>;
}

export default connect(undefined, {
  createContact: createContactWithPrescriptions,
  prescriptionsRE_HE,
  prescriptionsRE,
  prescriptionsHE,
})(MedicationCreate);
